import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { useCreateScenario, useScenarioDefaults } from '@/api/queries'
import { Button, Card, Field, Input, Skeleton } from '@/components/ui/primitives'
import {
  definitionFromDraft,
  draftFromDefaultSteps,
  validateDraft,
  type ScenarioDraft,
} from '@/features/scenarios/draft'
import { StepEditor } from '@/features/scenarios/step-editor'
import { apiErrorLabel } from '@/shared/labels'

export const Route = createFileRoute('/_auth/scenarios/new')({
  component: NewScenarioPage,
})

/**
 * Стартовый чек-лист шагов приходит с бэкенда (DEFAULT_REVIEW_STEPS), поэтому
 * форма монтируется только когда он загружен: начальное состояние задаётся
 * инициализатором useState, а не эффектом.
 */
function NewScenarioPage() {
  const defaults = useScenarioDefaults()

  if (!defaults.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-44" />
        <Skeleton className="h-24 max-w-lg" />
        <Skeleton className="h-52" />
      </div>
    )
  }

  return <NewScenarioForm initialDraft={draftFromDefaultSteps(defaults.data.steps)} />
}

function NewScenarioForm({ initialDraft }: { initialDraft: ScenarioDraft }) {
  const router = useRouter()
  const create = useCreateScenario()
  const [name, setName] = useState('')
  const [draft, setDraft] = useState<ScenarioDraft>(initialDraft)
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    if (!name.trim()) {
      setError('Укажите название сценария')
      return
    }
    const validationError = validateDraft(draft)
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    create.mutate(
      { name: name.trim(), definition: definitionFromDraft(draft) },
      {
        onSuccess: (scenario) => {
          toast.success('Сценарий создан. Чтобы применять его к разговорам, сделайте активным.')
          void router.navigate({
            to: '/scenarios/$scenarioId',
            params: { scenarioId: scenario.id },
          })
        },
        onError: (apiError) =>
          setError(
            apiErrorLabel((apiError as { detail?: unknown }).detail, 'Не удалось сохранить'),
          ),
      },
    )
  }

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/scenarios"
          className="inline-flex cursor-pointer items-center gap-1.5 text-[13px] text-ink-muted transition-colors hover:text-ink"
        >
          <ArrowLeft className="size-3.5" />
          Все сценарии
        </Link>
        <h1 className="mt-1.5 font-display text-xl font-bold text-ink">Новый сценарий</h1>
        <p className="mt-0.5 text-[13px] text-ink-subtle">
          Сценарий создаётся неактивным — активация отдельным действием.
        </p>
      </div>

      <Card className="max-w-lg p-4">
        <Field
          label="Название сценария"
          error={error?.includes('название') ? error : undefined}
          htmlFor="name"
        >
          <Input
            id="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Продажа матраса, весна 2026"
            autoComplete="off"
          />
        </Field>
      </Card>

      <StepEditor draft={draft} onChange={setDraft} />

      {error && !error.includes('название') && (
        <p
          role="alert"
          className="rounded-[var(--radius-control)] bg-negative-soft px-3 py-2 text-[13px] text-negative"
        >
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button variant="primary" loading={create.isPending} onClick={submit}>
          Создать сценарий
        </Button>
        <Link to="/scenarios">
          <Button>Отмена</Button>
        </Link>
      </div>
    </div>
  )
}
